import {Collapse, Form, Input, Select} from 'antd';
import React from 'react';

import {brandConfig, CONSTANTS, FormRow, generateAlphabetString} from '../../../../ui/src';
import {InfoIcon, SwitchRow} from '../';
import {STRINGS} from '../..';
import {CampaignTypes, ProfileBasedCampaigns} from '../../../../types/src';

const {Panel} = Collapse;

interface AdvancedOptionsProps {
  values: any;
  errors: any;
  touched: any;
  setFieldValue: (field: string, value: any) => void;
  client: any;
  trackIntercomEvent: (event: string, show: boolean, condition: boolean) => void;
  selectedType: CampaignTypes;
  clientTags?: any[];
  shouldShow: boolean;
  InstantlyFormItemContainer?: React.ComponentType<any>;
}

export const AdvancedOptions = ({
  values,
  errors,
  touched,
  setFieldValue,
  client, //  redux
  trackIntercomEvent, // CampaignsPage IntercomService
  selectedType, //type of campaign, what todo with it?
  clientTags, // instantly, move out? (redux)
  shouldShow,
  InstantlyFormItemContainer, //instantly, move out? (component) could be imported directly
}: AdvancedOptionsProps) => {
  return (
    <Collapse
      bordered={false}
      className="acquisition-details__advanced-options"
      style={{marginBottom: '20px', background: 'unset'}}
      defaultActiveKey={[]}
    >
      <Panel header="Advanced options" style={{border: 'none', margin: '0 -15px'}} key="1">
        {ProfileBasedCampaigns.includes(selectedType) && (
          <FormRow>
            <SwitchRow
              label={
                <span>
                Send via Sales Navigator: <InfoIcon message={STRINGS.tooltip.send_via_sales_navigator} />
              </span>
              }
              onChange={(checked: boolean) => setFieldValue('is_sn_inbox_type', checked)}
              checked={!!values.is_sn_inbox_type}
              isLock={false}
            />
          </FormRow>
        )}
        <FormRow>
          <Form.Item
            label={
              <span>
                Exclude Companies: <InfoIcon message={STRINGS.tooltip.exclude_companies} />
              </span>
            }
            validateStatus={errors.exclude_companies && touched.exclude_companies ? 'error' : ''}
            hasFeedback
            help={errors.exclude_companies && touched.exclude_companies ? errors.exclude_companies?.toString() : ''}
          >
            <Input
              name="exclude_companies"
              placeholder={STRINGS.placeholder.exclude_companies}
              value={values.exclude_companies}
              onChange={ev => setFieldValue('exclude_companies', ev.target.value)}
            />
          </Form.Item>
        </FormRow>
        <FormRow>
          <SwitchRow
            label={
              <span>
                Convert URLs in Trackable URLs:{' '}
                <InfoIcon
                  message={
                    <span>
                      By activating this feature, {brandConfig.name} transforms all URLs in your messages into trackable
                      links, granting you visibility into who is interacting with your content.
                      <br />
                      <br />
                      Here's how it works: Enable the feature and assign a short name to your link. {
                        brandConfig.name
                      }{' '}
                      then takes care of the rest. For instance, if your message says, “Hi, check out my website at
                      http://website.com,” and you've designated the short name “MarchPromo,” your message will be
                      updated to “Hi, check out my website at https://bitlyl.ink/MarchPromo6f21s.”
                      <br />
                      <br />
                      When a prospect clicks the link, they're seamlessly redirected to your original URL
                      (http://website.com in this example).
                      {!brandConfig.isAffiliate && (
                        <>
                          <span>Dive deeper into this feature at </span>
                          <a href={brandConfig.articles.trackableUrl}>
                            <b>Trackable URLs</b>
                          </a>{' '}
                          <span>to enhance your campaign tracking capabilities.</span>
                        </>
                      )}
                    </span>
                  }
                />
              </span>
            }
            onChange={(checked: boolean) =>
              setFieldValue(
                'tracking_link_prefix',
                checked ? generateAlphabetString(CONSTANTS.defaultTrackingLinkPrefixLength) : null
              )
            }
            checked={!!values.tracking_link_prefix}
            isLock={false}
          />
        </FormRow>
        {values.tracking_link_prefix && (
          <FormRow>
            <Form.Item
              label={''}
              validateStatus={errors.tracking_link_prefix && touched.tracking_link_prefix ? 'error' : ''}
              help={
                errors.tracking_link_prefix && touched.tracking_link_prefix
                  ? errors.tracking_link_prefix?.toString()
                  : ''
              }
            >
              <Input
                name="tracking_link_prefix"
                placeholder={'URL short name (e.g. MarchPromo)'}
                value={values.tracking_link_prefix}
                onChange={ev => setFieldValue('tracking_link_prefix', ev.target.value)}
              />
            </Form.Item>
          </FormRow>
        )}
        {values.tracking_link_prefix && client.attribution_feature && (
          <FormRow>
            <SwitchRow
              label={
                <span>
                  Adding UTM parameters to Trackable URLs:{' '}
                  <InfoIcon
                    message={
                      <span>By activating this feature, {brandConfig.name} adds utm params to trackable links.</span>
                    }
                  />
                </span>
              }
              onChange={(checked: boolean) => setFieldValue('add_attribution', checked ? 1 : 0)}
              checked={!!values.add_attribution}
              isLock={false}
            />
          </FormRow>
        )}

        {!client.feature_disabled && (
          <FormRow>
            <SwitchRow
              label={
                <span>
                  Exclude Responders: <InfoIcon message={STRINGS.tooltip.exclude_responders} />
                </span>
              }
              onChange={(checked: boolean) => setFieldValue('exclude_responders', checked ? 1 : 0)}
              checked={!!values.exclude_responders}
              isLock={false}
            />
          </FormRow>
        )}
        {client.instantly_feature && (
          <InstantlyFormItemContainer
            errors={errors}
            touched={touched}
            values={values}
            tags={clientTags}
            onChangeCampaigns={(values: string[]) => setFieldValue('instantly_campaign_ids', values)}
            onChangeTags={(values: string[]) => setFieldValue('instantly_tags_ids', values)}
          />
        )}
        {shouldShow && (
          <FormRow>
            <SwitchRow
              label={
                <span>
                  Invite people that require email using data credits:{' '}
                  <InfoIcon message={STRINGS.tooltip.send_invite_with_enhanced_email} />
                </span>
              }
              onChange={(checked: boolean) => setFieldValue('enhance_active', checked ? 1 : 0)}
              checked={!!values.enhance_active}
              isLock={client.data_credits <= 0}
              onLockClick={() => trackIntercomEvent('enhance_email_required_feature', true, true)}
            />
          </FormRow>
        )}
        <FormRow>
          <SwitchRow
            label={<span>Like Receivers Recent Post:</span>}
            onChange={(checked: boolean) => setFieldValue('like_receiver_post', checked ? 1 : 0)}
            checked={!!values.like_receiver_post}
            isLock={false}
          />
        </FormRow>
        {!['acquisition_message_request', 'acquisition_inmail'].includes(selectedType) && (
          <>
            <FormRow>
              <SwitchRow
                label={
                  <span>
                    Multiple Invites to Same Receiver: <InfoIcon message={STRINGS.tooltip.multiple_invites_active} />
                  </span>
                }
                onChange={(checked: boolean) => setFieldValue('multiple_invites_active', checked ? 1 : 0)}
                checked={!!values.multiple_invites_active}
                isLock={!client.multiple_invites_feature}
                onLockClick={() =>
                  trackIntercomEvent('multiple_invites_feature_request', true, !client.multiple_invites_feature)
                }
              />
            </FormRow>
            <FormRow>
              <SwitchRow
                label={
                  <span>
                    Auto Follow Connections on Twitter: <InfoIcon message={STRINGS.tooltip.twitter_follow} />
                  </span>
                }
                onChange={(checked: boolean) => setFieldValue('twitter_follow', checked ? 1 : 0)}
                checked={!!values.twitter_follow}
                isLock={!client.twitter_feature}
                onLockClick={() => trackIntercomEvent('twitter_feature_request', true, !client.twitter_feature)}
              />
            </FormRow>
          </>
        )}
        {shouldShow && (
          <FormRow>
            <Form.Item
              label={
                <span>
                  Invites Volume:{' '}
                  <InfoIcon
                    message={
                      <span>
                        {brandConfig.name} splits daily invites across campaigns evenly when default volume is "Normal".
                        For example, if a user has 100 daily maximum invites and two campaigns with volume "Normal",{' '}
                        {brandConfig.name} will send 50 invites in each campaign. <br />
                        Use High volume to double number invites, or Very High volume to triple. For example, if a user
                        has 100 daily maximum invites, two campaigns with volume "Normal" and one campaign with volume
                        "High", {brandConfig.name} will send 25 invites the two campaigns "Normal" and 50 with the
                        campaign "High". <br />
                        <br />
                        Note: setting all campaigns at level "High" will have same effect as setting them all at
                        "Normal". <br />
                        <br />
                        {!brandConfig.isAffiliate && `More info here: ${brandConfig.articles.invitesVolume}`}
                      </span>
                    }
                  />
                </span>
              }
            >
              <Select
                disabled={!client.invites_volume_feature}
                onChange={(value: any) => setFieldValue('invites_volume', value)}
                value={values.invites_volume}
                defaultValue={'1'}
                style={{width: 120}}
              >
                <Select.Option value="1">Normal</Select.Option>
                <Select.Option value="2">High</Select.Option>
                <Select.Option value="3">Very High</Select.Option>
              </Select>
            </Form.Item>
          </FormRow>
        )}
      </Panel>
    </Collapse>
  );
};
